import { useCallback } from 'react'
import { formatCurrencyValue, formatNumberForLocale } from '@vend/utilities'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../ng-compat/hooks'

export const DECIMAL_PLACES = 2

export const useFormatCurrencyValue = () => {
  const retailer = useRetailerSettingsManager().get()
  const currentOutlet = useRegisterManager().getActiveOutlet()

  return useCallback(
    (amount: number | string) => {
      return formatCurrencyValue(
        amount,
        retailer.culture,
        currentOutlet.currency,
        currentOutlet.currency_symbol
      )
    },
    [currentOutlet.currency, currentOutlet.currency_symbol, retailer.culture]
  )
}

export interface useFormatNumberProps {
  maximumFractionDigits?: number
  trimInsignificantDigits?: boolean
}

export const useFormatNumber = (props: useFormatNumberProps = {}) => {
  const retailer = useRetailerSettingsManager().get()

  return useCallback(
    (value: number | string) => {
      return formatNumberForLocale(value, retailer.culture, {
        style: 'decimal',
        maximumFractionDigits: props.maximumFractionDigits || DECIMAL_PLACES,
        minimumFractionDigits: props.trimInsignificantDigits
          ? 0
          : DECIMAL_PLACES,
        useGrouping: false,
      })
    },
    [
      props.maximumFractionDigits,
      props.trimInsignificantDigits,
      retailer.culture,
    ]
  )
}

export interface useFormatPercentageProps {
  minimumFractionDigits?: number
}

export const useFormatPercentage = (props: useFormatPercentageProps = {}) => {
  const retailer = useRetailerSettingsManager().get()
  const minimumFractionDigits = props.minimumFractionDigits ?? 0

  return useCallback(
    (value: number | string) => {
      return formatNumberForLocale(value, retailer.culture, {
        style: 'percent',
        maximumFractionDigits: DECIMAL_PLACES,
        minimumFractionDigits,
        useGrouping: false,
      })
    },
    [minimumFractionDigits, retailer.culture]
  )
}
